import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { getUserFacingErrorMessage } from '../utils/api';
import { UserContext } from '../App';
import NomineeShell from './NomineeShell';
import { Button, Card, Container, Modal, Stack, Text } from '../ui';
import NomineeEstateTabs from './NomineeEstateTabs';
import NomineeEstateHeading from './NomineeEstateHeading';

export default function NomineeEstateOther() {
  const { currentUser } = useContext(UserContext);
  const { ownerSub } = useParams();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachmentsBusy, setAttachmentsBusy] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState('');
  const [attachmentsData, setAttachmentsData] = useState(null); // { assetId, category, attachments: [] }

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const resp = await api.get('/nominee/other-summary', { params: { ownerSub } });
        setRows(Array.isArray(resp.data) ? resp.data : []);
      } catch (e) {
        setError(getUserFacingErrorMessage(e, {
          fallback: 'We could not load other assets. Please try again.',
          includeSupportForUnknown: false,
        }));
      } finally {
        setLoading(false);
      }
    })();
  }, [ownerSub]);

  const openAttachments = async (assetId) => {
    try {
      setAttachmentsOpen(true);
      setAttachmentsBusy(true);
      setAttachmentsError('');
      setAttachmentsData(null);
      const resp = await api.get('/nominee/other-attachments', { params: { ownerSub, assetId } });
      setAttachmentsData(resp.data || null);
    } catch (e) {
      setAttachmentsError(getUserFacingErrorMessage(e, {
        fallback: 'We could not load attachments. Please try again.',
        includeSupportForUnknown: false,
      }));
    } finally {
      setAttachmentsBusy(false);
    }
  };

  const downloadAttachment = async (objectId) => {
    const id = String(objectId || '').trim();
    if (!id) return;
    const r = await api.get(`/vault/objects/${encodeURIComponent(id)}/download`);
    const url = r?.data?.url;
    if (!url) throw new Error('No URL returned');
    window.location.assign(url);
  };

  if (!currentUser) return <div>Loading...</div>;
  if (currentUser.type !== 'nominee') {
    navigate('/dashboard');
    return null;
  }

  return (
    <NomineeShell>
      <div style={{ padding: '32px 0' }}>
        <Container>
          <Stack gap={16}>
            <NomineeEstateHeading ownerSub={ownerSub} title="Other assets" />

            <NomineeEstateTabs ownerSub={ownerSub} active="other" />

            <div>
              <Button type="button" variant="secondary" onClick={() => navigate('/nominee')}>
                Back to estates
              </Button>
            </div>

            {error && (
              <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                <div className="pv-error">{error}</div>
              </Card>
            )}

            {loading ? (
              <Text>Loading…</Text>
            ) : (
              <Card>
                <Stack gap={12}>
                  <Text as="div" weight={700}>Assets (redacted)</Text>
                  {rows.length === 0 ? (
                    <Text muted>No shared other assets.</Text>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: 'var(--mist-100)' }}>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Category</th>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Type</th>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Institution</th>
                            <th style={{ textAlign: 'left', padding: 12, borderBottom: '1px solid var(--mist-200)' }}>Attachments</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.id}>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.typeCategory || ''}</td>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.type || ''}</td>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>{r.institution || ''}</td>
                              <td style={{ padding: 12, borderBottom: '1px solid #f3f4f6' }}>
                                <Button type="button" variant="secondary" onClick={() => openAttachments(r.id)}>
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Stack>
              </Card>
            )}

            <Modal
              open={attachmentsOpen}
              onClose={() => { setAttachmentsOpen(false); setAttachmentsData(null); setAttachmentsError(''); }}
              title="Attachments"
              subtitle={attachmentsData?.category ? `Category: ${attachmentsData.category}` : 'Download shared attachments'}
            >
              {attachmentsError && (
                <Card style={{ background: 'var(--peach-50)', borderColor: 'var(--peach-300)' }}>
                  <div className="pv-error">{attachmentsError}</div>
                </Card>
              )}
              {attachmentsBusy ? (
                <Text>Loading…</Text>
              ) : (
                <div style={{ display: 'grid', gap: 10 }}>
                  {Array.isArray(attachmentsData?.attachments) && attachmentsData.attachments.length > 0 ? (
                    attachmentsData.attachments.map((a) => (
                      <Card key={a.objectId}>
                        <Stack gap={8}>
                          <Text as="div" weight={700}>{a.fileName || 'attachment'}</Text>
                          <Text size="sm" muted style={{ margin: 0 }}>
                            {(a.contentType || 'unknown type')}{a.bytes ? ` • ${a.bytes} bytes` : ''}
                          </Text>
                          <div>
                            <Button type="button" variant="primary" onClick={() => downloadAttachment(a.objectId)}>
                              Download
                            </Button>
                          </div>
                        </Stack>
                      </Card>
                    ))
                  ) : (
                    <Text muted>No attachments available.</Text>
                  )}
                </div>
              )}
            </Modal>
          </Stack>
        </Container>
      </div>
    </NomineeShell>
  );
}

